<template>
  <SectionShell id="features" num="06" kicker="The Feature Atlas">
    <template #title>Four features.<br />One <span class="sig">four-step</span><br />procedure. Forever.</template>
    <template #lede>
      <p>
        Nose, mouth, eyes, ears — they look like four different problems and they are
        not. Every one of them is built the same way: an accurate line drawing, then
        the darks, then the blend, then lift the lights back out. Learn the rhythm on
        the nose and the rest are variations.
      </p>
    </template>

    <div class="atlas">
      <!-- tabs -->
      <div class="tabs" role="tablist" aria-label="Feature">
        <button
          v-for="f in features"
          :key="f.id"
          role="tab"
          :aria-selected="String(active === f.id)"
          :class="{ 'is-on': active === f.id }"
          @click="active = f.id"
        >
          {{ f.name }}
        </button>
      </div>

      <div class="atlas__body">
        <!-- ---------- the drawing ---------- -->
        <figure class="study card">
          <div class="study__svgwrap">
            <svg viewBox="0 0 340 240" class="study__svg" role="img" :aria-label="current.alt">
              <defs>
                <filter id="fx-blend" x="-25%" y="-25%" width="150%" height="150%">
                  <feGaussianBlur stdDeviation="3.1" />
                </filter>
                <filter id="fx-soft" x="-25%" y="-25%" width="150%" height="150%">
                  <feGaussianBlur stdDeviation="2.2" />
                </filter>
                <radialGradient id="fx-hi">
                  <stop offset="0%" stop-color="#ffffff" stop-opacity="0.95" />
                  <stop offset="100%" stop-color="#ffffff" stop-opacity="0" />
                </radialGradient>
              </defs>

              <rect width="340" height="240" fill="#0b0f14" />

              <!-- ============ EYE ============ -->
              <g v-if="active === 'eye'">
                <g :style="shade">
                  <ellipse cx="170" cy="118" rx="126" ry="52" fill="#767d87" />
                  <path d="M44 118 Q120 60 200 70 Q256 78 296 112 Q250 96 170 96 Q100 98 44 118Z" fill="#3a4048" />
                  <ellipse cx="170" cy="112" rx="40" ry="40" fill="#4a515a" />
                  <circle cx="170" cy="112" r="40" fill="#2f353d" />
                  <path d="M30 56 Q150 22 300 62 L300 74 Q150 40 30 68Z" fill="#3a4048" />
                  <ellipse cx="170" cy="176" rx="86" ry="16" fill="#5c636c" opacity="0.7" />
                </g>
                <g :style="crisp">
                  <circle cx="170" cy="112" r="17" fill="#0a0c10" />
                  <path d="M44 118 Q120 62 200 70 Q256 78 296 112" stroke="#0a0c10" stroke-width="8" fill="none" stroke-linecap="round" />
                  <path d="M50 124 Q114 162 190 154 Q252 148 292 118" stroke="#2a313a" stroke-width="3" fill="none" stroke-linecap="round" />
                </g>

                <!-- lifted lights -->
                <g :style="lift">
                  <circle cx="182" cy="100" r="9" fill="#ffffff" />
                  <circle cx="182" cy="100" r="17" fill="url(#fx-hi)" opacity="0.5" />
                  <ellipse cx="158" cy="126" rx="12" ry="7" fill="#c9cfd6" opacity="0.55" filter="url(#fx-soft)" />
                  <ellipse cx="96" cy="132" rx="26" ry="8" fill="#e8ecf1" opacity="0.35" filter="url(#fx-soft)" />
                </g>

                <!-- line drawing -->
                <g :style="line" fill="none" stroke="#7fe9f5" stroke-width="1.3">
                  <path d="M44 118 Q120 60 200 70 Q256 78 296 112" />
                  <path d="M44 118 Q112 158 190 150 Q250 144 296 112" />
                  <path d="M50 124 Q114 164 190 156 Q252 150 292 118" stroke-dasharray="3 4" />
                  <circle cx="170" cy="112" r="40" />
                  <circle cx="170" cy="112" r="17" />
                  <circle cx="182" cy="100" r="9" />
                  <path d="M30 56 Q150 22 300 62" />
                  <path d="M44 118 L30 128 L52 130Z" />
                </g>

                <g :style="annot" font-family="'JetBrains Mono',monospace" font-size="8.5" fill="#8b96a5" letter-spacing="1">
                  <text x="86" y="18">one catchlight, half in the pupil</text>
                  <text x="10" y="200">lower lid thickness</text>
                  <text x="10" y="216">lashes grow off the lid, not the eye</text>
                </g>
              </g>

              <!-- ============ NOSE ============ -->
              <g v-else-if="active === 'nose'">
                <g :style="shade">
                  <path d="M148 20 Q136 96 120 138 Q108 168 132 186 Q170 200 208 186 Q232 168 220 138 Q204 96 192 20Z" fill="#8a919b" />
                  <path d="M184 24 Q198 100 216 140 Q230 166 206 184 Q186 194 168 194 Q204 176 206 150 Q204 112 178 28Z" fill="#333a43" />
                  <ellipse cx="168" cy="156" rx="34" ry="27" fill="#a6adb6" />
                  <ellipse cx="130" cy="172" rx="22" ry="17" fill="#6c737d" />
                  <ellipse cx="210" cy="172" rx="22" ry="17" fill="#3f4650" />
                  <ellipse cx="180" cy="204" rx="54" ry="12" fill="#242a32" />
                </g>
                <g :style="crisp">
                  <ellipse cx="132" cy="177" rx="9" ry="5.6" fill="#15191f" transform="rotate(-16 132 177)" />
                  <ellipse cx="208" cy="177" rx="9" ry="5.6" fill="#0d1015" transform="rotate(16 208 177)" />
                </g>

                <g :style="lift">
                  <ellipse cx="158" cy="146" rx="17" ry="13" fill="#f8fafc" opacity="0.8" filter="url(#fx-soft)" />
                  <ellipse cx="150" cy="70" rx="8" ry="34" fill="#dfe4e9" opacity="0.4" filter="url(#fx-soft)" />
                  <ellipse cx="118" cy="172" rx="9" ry="5" fill="#dfe4e9" opacity="0.4" filter="url(#fx-soft)" />
                </g>

                <g :style="line" fill="none" stroke="#7fe9f5" stroke-width="1.3">
                  <circle cx="168" cy="156" r="34" stroke-dasharray="3 4" />
                  <circle cx="130" cy="172" r="22" stroke-dasharray="3 4" />
                  <circle cx="210" cy="172" r="22" stroke-dasharray="3 4" />
                  <path d="M148 20 Q136 96 120 138 Q108 168 132 186 Q170 200 208 186 Q232 168 220 138 Q204 96 192 20" />
                  <ellipse cx="132" cy="177" rx="9" ry="5.6" transform="rotate(-16 132 177)" />
                  <ellipse cx="208" cy="177" rx="9" ry="5.6" transform="rotate(16 208 177)" />
                </g>

                <g :style="annot" font-family="'JetBrains Mono',monospace" font-size="8.5" fill="#8b96a5" letter-spacing="1">
                  <text x="10" y="16">three spheres: one ball, two wings</text>
                  <text x="10" y="228">nostrils are shapes, not holes</text>
                </g>
              </g>

              <!-- ============ MOUTH ============ -->
              <g v-else-if="active === 'mouth'">
                <g :style="shade">
                  <ellipse cx="170" cy="126" rx="128" ry="62" fill="#767d87" />
                  <path d="M46 108 Q104 78 138 92 Q156 68 170 88 Q184 68 202 92 Q236 78 294 108 Q230 122 170 118 Q110 122 46 108Z" fill="#3a4048" />
                  <path d="M46 108 Q118 128 170 122 Q222 128 294 108 Q240 174 170 176 Q100 174 46 108Z" fill="#8d949d" />
                  <ellipse cx="170" cy="196" rx="76" ry="16" fill="#5c636c" opacity="0.75" />
                  <ellipse cx="170" cy="66" rx="46" ry="16" fill="#666d76" opacity="0.6" />
                </g>
                <g :style="crisp">
                  <path d="M46 108 Q120 124 170 119 Q220 124 294 108 Q222 116 170 114 Q118 116 46 108Z" fill="#0a0c10" />
                  <path d="M46 108 Q38 100 44 116 Q56 118 60 110Z" fill="#0a0c10" />
                  <path d="M294 108 Q302 100 296 116 Q284 118 280 110Z" fill="#0a0c10" />
                </g>

                <g :style="lift">
                  <ellipse cx="164" cy="146" rx="34" ry="10" fill="#ffffff" opacity="0.8" filter="url(#fx-soft)" />
                  <ellipse cx="164" cy="146" rx="52" ry="18" fill="url(#fx-hi)" opacity="0.35" />
                  <path d="M56 112 Q120 128 170 124" stroke="#dfe4e9" stroke-width="3" fill="none" opacity="0.3" filter="url(#fx-soft)" />
                </g>

                <g :style="line" fill="none" stroke="#7fe9f5" stroke-width="1.3">
                  <path d="M46 108 Q104 78 138 92 Q156 68 170 88 Q184 68 202 92 Q236 78 294 108" />
                  <path d="M46 108 Q120 124 170 119 Q220 124 294 108" />
                  <path d="M46 108 Q120 174 170 176 Q220 174 294 108" />
                  <path d="M150 40 L150 74 M190 40 L190 74" stroke-dasharray="3 4" />
                </g>

                <g :style="annot" font-family="'JetBrains Mono',monospace" font-size="8.5" fill="#8b96a5" letter-spacing="1">
                  <text x="10" y="18">the philtrum reads as a U</text>
                  <text x="10" y="228">a pit at each corner sinks the mouth in</text>
                </g>
              </g>

              <!-- ============ EAR ============ -->
              <g v-else>
                <g :style="shade">
                  <!-- the head plane behind the ear, in shadow -->
                  <path d="M200 16 Q226 120 196 224 L344 248 L344 -8Z" fill="#252b33" opacity="0.8" />
                  <!-- ear body -->
                  <path d="M196 40 Q178 26 150 26 Q126 34 120 70 Q108 110 124 152 Q136 196 162 212 Q186 214 188 186 Q196 150 192 120 Q196 70 196 40Z" fill="#7b828c" />
                  <!-- inside the helix rim, a step darker -->
                  <path d="M188 56 Q174 42 152 42 Q136 50 132 78 Q122 112 136 150 Q147 184 164 198 Q180 196 180 176 Q186 146 182 118 Q188 82 188 56Z" fill="#4e555f" />
                  <!-- concha bowl: the deepest note in the ear -->
                  <path d="M172 98 Q188 104 188 132 Q188 160 172 170 Q158 160 160 132 Q160 106 172 98Z" fill="#242a32" />
                  <!-- antihelix ridge, catching light -->
                  <path d="M180 82 Q158 92 154 116 Q150 142 163 166" stroke="#9aa2ac" stroke-width="9" fill="none" stroke-linecap="round" />
                  <path d="M180 82 Q168 68 154 72" stroke="#8d949e" stroke-width="7" fill="none" stroke-linecap="round" />
                  <!-- lobe -->
                  <path d="M148 184 Q166 180 177 191 Q181 206 165 211 Q147 206 148 184Z" fill="#868d97" />
                  <!-- shadow the ear casts onto the head -->
                  <path d="M196 42 Q214 118 190 206 Q212 208 218 130 Q216 62 204 40Z" fill="#0d1116" opacity="0.9" />
                </g>

                <g :style="lift">
                  <path d="M130 62 Q116 106 126 150" stroke="#e8ecf1" stroke-width="5" fill="none" opacity="0.5" filter="url(#fx-soft)" />
                  <path d="M158 96 Q152 124 160 152" stroke="#f4f6f8" stroke-width="3.5" fill="none" opacity="0.45" filter="url(#fx-soft)" />
                  <ellipse cx="168" cy="196" rx="9" ry="6" fill="#eef1f4" opacity="0.4" filter="url(#fx-soft)" />
                </g>

                <g :style="crisp">
                  <path d="M186 130 Q198 134 196 150 Q186 158 181 148Z" fill="#1a1f26" />
                </g>

                <g :style="line" fill="none" stroke="#7fe9f5" stroke-width="1.3">
                  <path d="M196 40 Q178 26 150 26 Q126 34 120 70 Q108 110 124 152 Q136 196 162 212 Q186 214 188 186 Q196 150 192 120 Q196 70 196 40Z" />
                  <path d="M188 56 Q174 42 152 42 Q136 50 132 78 Q122 112 136 150 Q147 184 164 198" />
                  <path d="M180 82 Q158 92 154 116 Q150 142 163 166 M180 82 Q168 68 154 72" />
                  <path d="M172 98 Q188 104 188 132 Q188 160 172 170 Q158 160 160 132 Q160 106 172 98Z" />
                  <path d="M148 184 Q166 180 177 191 Q181 206 165 211 Q147 206 148 184Z" />
                  <path d="M232 34 L316 34 M232 200 L316 200" stroke-dasharray="3 4" />
                </g>

                <g :style="annot" font-family="'JetBrains Mono',monospace" font-size="8.5" fill="#8b96a5" letter-spacing="1">
                  <text x="238" y="28">top = brow line</text>
                  <text x="238" y="216">base = nose line</text>
                  <text x="10" y="230">from the front you see it at an angle</text>
                </g>
              </g>
            </svg>
          </div>

          <figcaption class="study__cap">
            <div class="steps">
              <button
                v-for="(s, i) in stepNames"
                :key="s"
                :class="{ 'is-on': step === i + 1 }"
                :aria-pressed="String(step === i + 1)"
                @click="step = i + 1"
              >
                {{ i + 1 }}. {{ s }}
              </button>
            </div>
            <label class="scrub">
              <span class="mono">scrub</span>
              <input type="range" min="1" max="4" step="1" v-model.number="step" aria-label="Build step" />
            </label>
          </figcaption>
        </figure>

        <!-- ---------- the notes ---------- -->
        <div class="notes">
          <transition name="swap" mode="out-in">
            <div :key="active">
              <span class="notes__k mono">{{ current.name }} — {{ stepNames[step - 1] }}</span>
              <h3>{{ current.headline }}</h3>
              <p class="notes__step">{{ current.steps[step - 1] }}</p>
              <ul class="rules">
                <li v-for="r in current.rules" :key="r"><span v-html="r"></span></li>
              </ul>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </SectionShell>
</template>

<script setup>
import { ref, computed } from 'vue'
import SectionShell from '../ui/SectionShell.vue'

const active = ref('nose')
const step = ref(1)
const stepNames = ['line', 'darks', 'blend', 'lift']

/* how the three drawing layers respond to the step scrubber */
const line = computed(() => ({
  opacity: step.value === 1 ? 1 : step.value === 2 ? 0.28 : 0,
  transition: 'opacity .45s'
}))
const shade = computed(() => ({
  opacity: step.value >= 2 ? 1 : 0.12,
  filter: step.value >= 3 ? 'url(#fx-blend)' : 'none',
  transition: 'opacity .45s, filter .45s'
}))
const lift = computed(() => ({
  opacity: step.value >= 4 ? 1 : 0,
  transition: 'opacity .5s'
}))
/* Some marks never get blended away — a pupil, the line between the lips,
   the inside of a nostril. They stay out of the blur filter entirely. */
const crisp = computed(() => ({
  opacity: step.value >= 2 ? 1 : 0.12,
  transition: 'opacity .45s'
}))
const annot = computed(() => ({ opacity: step.value === 1 ? 1 : 0.22, transition: 'opacity .45s' }))

const features = [
  {
    id: 'nose',
    name: 'Nose',
    alt: 'A nose built from three spheres, shaded in four steps',
    headline: 'Three spheres wearing a bridge.',
    steps: [
      'Draw the ball of the nose and the two wings as overlapping circles, then find the line where the bridge meets them. Nothing here is a "nose" yet — it is three circles and two soft edges.',
      'Darkest values first: inside each nostril and the cast shadow the nose throws onto the upper lip. Then the #2 down the shadowed side of the bridge and under the ball.',
      'Blend around the curves, never straight across. The bridge blends vertically, the ball blends around its roundness, the wings blend along their curl.',
      'Lift the highlight off the ball and a thin one down the lit side of the bridge. That single bright spot is what turns a grey lump into cartilage.'
    ],
    rules: [
      'The nose is defined almost entirely by <b>shadow</b>. There is no outline anywhere on a real nose.',
      'Nostrils are rarely symmetrical and never pure black holes — they are dark shapes with soft outer edges.',
      'If the nose looks pasted on, you are missing the cast shadow it drops onto the lip.'
    ]
  },
  {
    id: 'mouth',
    name: 'Mouth',
    alt: 'A closed mouth built in four steps, showing the pit and the lower-lip highlight',
    headline: 'A squashed M, a wandering line, and two pits.',
    steps: [
      'The top edge of the upper lip is a flattened M. The line between the lips is irregular — thick in places, hair-thin in others. Draw that line honestly and the mouth is already half-right.',
      'The darkest value is the line between the lips and the two corner pits. The upper lip is almost always darker than the lower, because it angles away from the light.',
      'Blend the upper lip toward the philtrum and the lower lip downward into the chin. Keep a reserved patch on the lower lip — do not blend over the shine.',
      'Lift the highlight on the lower lip with a pointed kneaded eraser, plus a faint rim of reflected light along the outer lip edge. That is what makes lips look moist.'
    ],
    rules: [
      'Never fence the lips with a line. Women\'s lips can read as outlined because of make-up — soften it to an <b>edge</b>, not a stroke.',
      'The <b>pit</b> at each corner is a small comma of dark. Without it, a mouth looks stuck to the front of the face.',
      'Teeth are shapes with soft grey separations. A dark line between each one gives you corn on the cob.'
    ]
  },
  {
    id: 'eye',
    name: 'Eyes',
    alt: 'An eye built in four steps, showing iris, pupil, catchlight and lid thickness',
    headline: 'Eleven puzzle pieces, two perfect circles.',
    steps: [
      'Iris and pupil go in with the circle template — same hole for both eyes. Then the upper lash line, the lower lid thickness, the corner membrane, the brow shape.',
      'Fill the pupil to true black and darken the outer rim of the iris. Lay the lash line in: heaviest above the iris, tapering to nothing at both corners.',
      'Blend the iris to a halftone, then blend a whisper of tone into the white of the eye — a truly white sclera makes the eye look flat and pasted-on.',
      'Lift a soft light in the lower iris opposite the catchlight, then place the one catchlight: half in the pupil, half in the iris. One per eye, no matter how many the photo shows.'
    ],
    rules: [
      'Eyes work as a pair. If they are not looking at exactly the same thing, nothing else you fix will save the portrait.',
      'Lashes grow in <b>clumps</b>, curve, and taper. Flick the stroke so it thins at the tip; lower lashes come off the lid thickness and are shorter.',
      'A turned head turns the iris into an <b>ellipse</b>. Circles on a three-quarter face are an instant tell.'
    ]
  },
  {
    id: 'ear',
    name: 'Ears',
    alt: 'An ear built in four steps, with brow-line and nose-line placement guides',
    headline: 'A bowl inside a rim inside a spiral.',
    steps: [
      'Outer helix first, then the antihelix folded inside it, then the bowl of the concha, the tragus and the lobe. Treat it as nested shapes, not as "an ear".',
      'The concha is the darkest area — a deep cup. Add the shadow behind the ear where it meets the skull, and the dark under the tragus.',
      'Blend everything to a mid halftone first, then push the darks back in. Ears are all bumps, so the tone must roll rather than sit flat.',
      'Lift the rim of the helix and the front edge of the antihelix. Cartilage catches light on every ridge — that is what makes an ear look like an ear.'
    ],
    rules: [
      'Placement: the top of the ear lines up with the <b>brow</b>, the bottom with the <b>base of the nose</b>.',
      'Seen from the front the ear is angled away from you, so you never see its full shape.',
      'In most portraits it is half-covered by hair anyway — which is excellent news.'
    ]
  }
]

const current = computed(() => features.find((f) => f.id === active.value))
</script>

<style scoped>
.tabs { display: flex; gap: 4px; margin-bottom: 22px; flex-wrap: wrap; }
.tabs button { padding: 11px 20px; }

.atlas__body {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  gap: clamp(24px, 3.5vw, 56px);
  align-items: start;
}

.study { margin: 0; overflow: hidden; position: sticky; top: 92px; background: #0b0f14; }
.study__svg { display: block; width: 100%; height: auto; }
.study__cap {
  border-top: 1px solid var(--edge-soft);
  padding: 12px 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
  justify-content: space-between;
}
.steps { display: flex; gap: 4px; flex-wrap: wrap; }
.steps button { padding: 7px 12px; font-size: 9px; }
.scrub { display: flex; align-items: center; gap: 12px; min-width: 160px; flex: 1; }
.scrub span { color: var(--text-faint); font-size: 9px; }

.notes__k { color: var(--signal); font-size: 10px; display: block; margin-bottom: 14px; }
.notes h3 {
  font-size: clamp(22px, 1.8vw + 14px, 33px);
  font-weight: 400;
  letter-spacing: -0.025em;
  margin-bottom: 18px;
}
.notes__step { color: var(--text); min-height: 108px; }

.rules { list-style: none; margin: 26px 0 0; padding: 0; border-top: 1px solid var(--edge-soft); }
.rules li {
  padding: 15px 0 15px 26px;
  border-bottom: 1px solid var(--edge-soft);
  color: var(--text-dim);
  font-size: 13.5px;
  line-height: 1.6;
  position: relative;
}
.rules li::before {
  content: '';
  position: absolute;
  left: 2px;
  top: 22px;
  width: 12px;
  height: 1px;
  background: var(--light);
}
.rules :deep(b) { color: var(--light); font-weight: 400; }

.swap-enter-active, .swap-leave-active { transition: opacity 0.28s, transform 0.28s; }
.swap-enter-from { opacity: 0; transform: translateY(10px); }
.swap-leave-to { opacity: 0; transform: translateY(-10px); }

@media (max-width: 980px) {
  .atlas__body { grid-template-columns: 1fr; }
  .study { position: static; }
}
</style>
